// `React` must be in SCOPE, not merely resolvable: the kiosk renderchecks run ts-node with
// `"jsx": "react"` (the classic runtime), which compiles this file's JSX to
// `React.createElement`. The app bundlers use the automatic runtime and need no import, so a
// missing one here builds clean and throws only in those node-only gates.
import React from "react";
import Svg, { Path, Rect } from "react-native-svg";

/**
 * The close affordance: a ✕ on a tinted disc.
 *
 * Arrived as the designer's export pasted whole into a `.tsx` — an `<svg>` element at a
 * module's top level, which is not a component — with both colours baked in.
 *
 * ## It draws the DISC as well as the mark, and that is the point
 *
 * Five surfaces had built this affordance by hand and no two agreed: discs of 28, 32, 36, 44
 * and 52 in four different greys, carrying a `✕` or a `×` set at 13, 14, 40 or `variant="heading"`
 * — so the mark's size relative to its disc was different in every modal, and one of them drew
 * the glyph muted on a grey plate. Here the ✕ is 32% of the box at every size, because both
 * parts scale with the one `viewBox`.
 *
 * A caller that genuinely owns its own chrome passes `withBackground={false}` and gets the mark
 * alone; a caller with a brand-tinted disc passes `backgroundColor` + `backgroundOpacity` rather
 * than painting its own behind this one. The alternative — every caller drawing a `<View>` disc
 * and this drawing the ✕ — is what was already there, and it is what drifted.
 *
 * 🔴 Worth keeping in view where the disc ends up: painted on a `Pressable` that carries
 * `accessibilityRole="button"`, a `backgroundColor` is erased on web by the app's Tailwind
 * preflight (CLAUDE.md). A disc drawn INSIDE an `<svg>` is not a background and no tag selector
 * reaches it, so this component sidesteps that hazard rather than having to dodge it.
 */
export function CrossIcon({
  size = 32,
  color = "#111C36",
  backgroundColor = "#111C36",
  backgroundOpacity = 0.08,
  withBackground = true,
}: {
  /** The whole affordance — disc included. The mark follows at 32% of it. */
  size?: number;
  /** The ✕ itself. */
  color?: string;
  /** The disc. Pass a solid colour with `backgroundOpacity={1}` to tint it with the brand. */
  backgroundColor?: string;
  backgroundOpacity?: number;
  /** For a caller that already draws its own disc and wants only the mark. */
  withBackground?: boolean;
}) {
  return (
    <Svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      {withBackground ? (
        <Rect
          width={32}
          height={32}
          rx={16}
          fill={backgroundColor}
          fillOpacity={backgroundOpacity}
        />
      ) : null}
      <Path
        d="M21.1998 11.8934L20.1638 10.8574L16.0569 14.9643L11.9501 10.8574L10.9141 11.8934L15.0209 16.0003L10.9141 20.1071L11.9501 21.1431L16.0569 17.0363L20.1638 21.1431L21.1998 20.1071L17.0929 16.0003L21.1998 11.8934Z"
        fill={color}
      />
    </Svg>
  );
}
CrossIcon.displayName = "CrossIcon";
export default CrossIcon;
